import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../../auth-config';
import { environment } from '../../../environments/environment';
import {
  buildLocalStorageKeyMaterial,
  deriveLocalStorageEncryptionKey,
} from './local-storage-crypto.util';
import { setLocalStorageEncryptionKey } from './local-storage-key-state.util';
import { migrateAllPlainLocalStorageKeys } from './local-storage-shim.util';

/**
 * Key 1.0.0 — Create secret password on page refresh (F5), before Angular app starts.
 *
 * Why: On refresh, screens may use localStorage before login service runs.
 * This reads Microsoft login from browser cache and creates password early.
 *
 * No parameters — reads Microsoft account from browser cache directly.
 *
 * Steps inside:
 *   1. Find logged-in Microsoft account
 *   2. Key 1.0.1 — join account ids + environment.clientId
 *   3. Key 1.0.2 — make password from joined text
 *   4. Key 1.0.3 — save password in memory
 *   5. Convert old plain localStorage data to encrypted
 *
 * Called from: main.ts
 */
export async function bootstrapLocalStorageEncryptionFromMsal(): Promise<void> {
  try {
    // Create Microsoft login client using app config
    const msalInstance = new PublicClientApplication(msalConfig);
    // Wait until Microsoft login library is ready
    await msalInstance.initialize();

    // Get logged-in user — active account first, otherwise first account in list
    const account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

    // No user logged in — stop here, password will be made on login
    if (!account) {
      return;
    }

    // Set this account as active if none is set
    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(account);
    }

    // Key 1.0.1 — homeAccountId and localAccountId from Microsoft account, clientId from environment
    const material = buildLocalStorageKeyMaterial(
      account.homeAccountId ?? '',
      account.localAccountId ?? '',
      environment.clientId
    );
    // Key 1.0.2 make password → Key 1.0.3 save in memory
    setLocalStorageEncryptionKey(deriveLocalStorageEncryptionKey(material));
    // Convert any old plain localStorage keys to encrypted
    migrateAllPlainLocalStorageKeys();
  } catch {
    // If failed, password will be created later when user logs in through auth.service
  }
}
